import { useCallback, useEffect, useRef, useState } from "react";

const MAX_CONCURRENT_DOWNLOADS = 4;

export function useLazyBackgroundImages(
  imageIds: string[],
  priorityImageId?: string,
) {
  const frameNodes = useRef(new Map<string, HTMLDivElement>());
  const [visibleIds, setVisibleIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [startedIds, setStartedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [loadedImagesStatus, setLoadedImagesStatus] = useState<
    Record<string, "error" | "loaded">
  >({});

  const imageIdsKey = imageIds.join(",");

  const markLoaded = useCallback((imageId: string) => {
    setLoadedImagesStatus((prev) =>
      prev[imageId] === "loaded" ? prev : { ...prev, [imageId]: "loaded" },
    );
  }, []);

  const markError = useCallback((imageId: string) => {
    setLoadedImagesStatus((prev) =>
      prev[imageId] === "error" ? prev : { ...prev, [imageId]: "error" },
    );
  }, []);

  const setFrameNode = useCallback(
    (imageId: string, node: HTMLDivElement | null) => {
      if (node) frameNodes.current.set(imageId, node);
      else frameNodes.current.delete(imageId);
    },
    [],
  );

  useEffect(() => {
    const nodes = [...frameNodes.current.entries()];
    if (nodes.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleIds((prev) => {
          const next = new Set(prev);
          let changed = false;
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const imageId = (entry.target as HTMLElement).dataset.imageId;
            if (!imageId || next.has(imageId)) continue;
            next.add(imageId);
            changed = true;
          }
          return changed ? next : prev;
        });
      },
      { rootMargin: "200px" },
    );

    for (const [imageId, node] of nodes) {
      node.dataset.imageId = imageId;
      observer.observe(node);
    }

    return () => observer.disconnect();
  }, [imageIdsKey]);

  useEffect(() => {
    if (imageIds.length === 0) return;

    const known = new Set(imageIds);
    const active = [...startedIds].filter(
      (imageId) => known.has(imageId) && !loadedImagesStatus[imageId],
    ).length;
    const next: string[] = [];
    const alreadyQueued = (imageId: string) =>
      startedIds.has(imageId) ||
      !!loadedImagesStatus[imageId] ||
      next.includes(imageId);

    if (
      priorityImageId &&
      known.has(priorityImageId) &&
      !alreadyQueued(priorityImageId)
    ) {
      next.push(priorityImageId);
    }

    let room = Math.max(0, MAX_CONCURRENT_DOWNLOADS - active - next.length);
    for (const imageId of imageIds) {
      if (room <= 0) break;
      if (!visibleIds.has(imageId) || alreadyQueued(imageId)) continue;
      next.push(imageId);
      room -= 1;
    }

    if (next.length === 0) return;
    setStartedIds((prev) => {
      const merged = new Set(prev);
      for (const imageId of next) merged.add(imageId);
      return merged;
    });
  }, [
    imageIdsKey,
    loadedImagesStatus,
    priorityImageId,
    startedIds,
    visibleIds,
  ]);

  return { setFrameNode, startedIds, loadedImagesStatus, markLoaded, markError };
}
